<script setup lang="ts">
import { computed } from 'vue'

import TnxLink from '~/components/link/TnxLink.vue'
import LoadingTable from '~/components/loading/LoadingTable.vue'
import NoDataPreview from '~/components/preview/NoDataPreview.vue'
import CustomSuspense from '~/components/suspense/CustomSuspense.vue'
import { BURN_HISTORY_COLUMN } from '~/components/table/burn-history/burnHistoryColumn.const'
import { useTableColumn } from '~/composables/useTable'
import { UNIT } from '~/environment-variables/unit.const'
import { TokenTransferModel } from '~/types/model/token.model'
import { isEmpty } from '~/utils/judge'

import BcTable from '~/ui/Table/BcTable.vue'

interface IProps {
  rows: TokenTransferModel[]
  isLoading: boolean
  rowCnt: number
}

withDefaults(defineProps<IProps>(), {
  rows: () => [],
})

// Age/Datetime 토글 로직
const localStorageKey = 'burn-history'
const hideAge = ref(false)

if (import.meta.client) {
  hideAge.value = JSON.parse(window.localStorage.getItem(localStorageKey) || 'false')
}

const toggle = () => {
  if (import.meta.client) {
    hideAge.value = !hideAge.value
    window.localStorage.setItem(localStorageKey, JSON.stringify(hideAge.value))
  }
}

const exceptList = computed(() => {
  const targetColumn = hideAge.value ? 'age' : 'unixTimeStamp'
  return [targetColumn]
})

const { column: cols } = useTableColumn(BURN_HISTORY_COLUMN, exceptList)
</script>

<template>
  <CustomSuspense
    :is-loading="isLoading"
    :is-empty="isEmpty(rows)"
  >
    <template #loading>
      <LoadingTable
        :row-cnt="rowCnt"
        :cols="cols"
      />
    </template>
    <template #empty>
      <NoDataPreview />
    </template>
    <template #default>
      <BcTable
        :cols="cols"
        :rows="rows"
        @change-table-data="toggle"
      >
        <template #transactionHash="{ row }: { row: TokenTransferModel }">
          <TnxLink
            class="tx-link"
            :hash="row.transactionHash"
            v-tooltip="row.transactionHash"
          />
        </template>

        <template #age="{ row }: { row: TokenTransferModel }">
          <span class="cell-muted">{{ row.age }}</span>
        </template>

        <template #unixTimeStamp="{ row }: { row: TokenTransferModel }">
          <span class="cell-muted">{{ row.unixTimeStamp }}</span>
        </template>

        <template #amount="{ row }: { row: TokenTransferModel }">
          <div class="amount-cell">
            <span class="cell-muted">{{ row.amount }}</span>
            <span class="cell-muted">{{ row.tokenInfo.symbol || UNIT }}</span>
          </div>
        </template>
      </BcTable>
    </template>
  </CustomSuspense>
</template>

<style scoped lang="scss">
.tx-link {
  @apply text-explorer-blue truncate min-w-0;
}

.cell-muted {
  @apply text-primary/60;
}

.amount-cell {
  @apply flex items-center gap-1;
}
</style>

